/**
 * @file intelLayout.ts
 * @brief U4: the Meeting Intelligence Intel / Points tab layout, per user.
 *
 * @details
 * The user can collapse a section of the Intel or Points tab and move it up
 * or down. The layout is one preference per USER on the server
 * (`GET / PUT /api/auth/intel-layout`, BACKEND U4-BE c9767f3), the same on
 * phone, web and Mac, for every meeting. Contract (copy it, never improvise):
 * workspace docs/product/intel-layout-u4.md.
 *
 * Pure: the merge (`applyOrder`, the backend's applyIntelLayoutOrder), the
 * Points key, the moves and the value a save sends. The fetch and the save
 * live in useIntelLayout.ts.
 *
 * Moves work on UNITS, the blocks the screen draws: one section each, except
 * the web's Attached notes card, which holds Agenda and Private notes
 * together (both keys move as one). Hidden (empty) sections keep their slot:
 * a move only shuffles the visible units through the visible slots.
 *
 * @references
 * - ./useIntelLayout.ts, ./components/IntelLayoutHeading.tsx
 * - BACKEND/src/features/auth/intelLayout.mjs (applyIntelLayoutOrder)
 * - FRONTEND (U4-P) the phone's copy of the same rules
 *
 * @author DES
 * @date 03-11-2026
 */

export type IntelLayoutTab = "intel" | "points";

export interface TabLayout {
  order: string[];
  collapsed: string[];
}

export interface IntelLayout {
  intel: TabLayout | null;
  points: TabLayout | null;
  updatedAt?: string;
}

/** The Intel tab's sections in today's order (the contract's fixed ids). */
export const INTEL_DEFAULT_ORDER = ["overview", "summary", "decisions", "attention", "agenda", "notes"] as const;
export type IntelSectionKey = (typeof INTEL_DEFAULT_ORDER)[number];

/** The server's caps per list: Intel never gets near 24; Points drops the oldest topics past 60. */
export const TAB_KEY_CAP: Record<IntelLayoutTab, number> = { intel: 24, points: 60 };

export type MoveAction = "up" | "down" | "top";

// eslint-disable-next-line no-control-regex -- matching control characters is the point
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/;

/**
 * A Points topic heading → its layout key, normalised exactly like
 * groupKeyPoints' map key, then cut to 64 code points (never a string slice:
 * that can split an emoji, and the server rejects a lone surrogate) and
 * trimmed again (a cut can leave a trailing space). null = a key the server
 * would refuse: that topic keeps today's plain heading. Same as the phone's
 * pointsTopicKey (FRONTEND src/features/intelLayout/intelLayout.ts).
 */
export function pointsKey(topic: string): string | null {
  const key = Array.from(topic.replace(/\s+/g, " ").trim().toLowerCase()).slice(0, 64).join("").trim();
  if (!key || CONTROL_CHARS.test(key)) return null;
  return key;
}

/**
 * One key per Points topic heading, in order; null for the no-topic group,
 * a refused key, or a repeat of an earlier topic's key (those keep a plain
 * heading: no arrow, no menu, never moved). Same rule as the phone.
 */
export function pointsSectionKeys(topics: readonly (string | null)[]): (string | null)[] {
  const seen = new Set<string>();
  return topics.map((topic) => {
    const key = topic ? pointsKey(topic) : null;
    if (!key || seen.has(key)) return null;
    seen.add(key);
    return key;
  });
}

/**
 * The keyed sections take the keyed slots in the user's order; plain
 * headings and the no-topic group stay exactly where they are (the phone's
 * rule). `ordered` = applyOrder over the keyed sections.
 */
export function placeKeyed<T>(items: readonly { key: string | null; item: T }[], ordered: readonly string[]) {
  const byKey = new Map(items.flatMap((x) => (x.key ? [[x.key, x.item] as const] : [])));
  let slot = 0;
  return items.map((x) => {
    if (!x.key) return x;
    const key = ordered[slot++];
    return { key, item: byKey.get(key) as T };
  });
}

/**
 * The contract's merge. `present` = this meeting's keys in today's order,
 * empty (hidden) sections included. The saved keys take the slots the saved
 * keys hold in the default order, in the saved order; the rest stay put.
 * Saved keys this meeting does not have are ignored here.
 */
export function applyOrder(present: readonly string[], saved?: readonly string[] | null): string[] {
  if (!saved?.length) return [...present];
  const here = new Set(present);
  const wanted = saved.filter((k) => here.has(k));
  const listed = new Set(wanted);
  let i = 0;
  return present.map((k) => (listed.has(k) ? wanted[i++] : k));
}

/** Only well-formed tab values count; anything else is the default. */
export function readTabLayout(raw: unknown): TabLayout | null {
  if (!raw || typeof raw !== "object") return null;
  const { order, collapsed } = raw as { order?: unknown; collapsed?: unknown };
  const strings = (v: unknown) => (Array.isArray(v) ? v.filter((s): s is string => typeof s === "string") : []);
  return { order: strings(order), collapsed: strings(collapsed) };
}

/** `{ success, layout }` → the layout, or null (nothing saved / not understood). */
export function readIntelLayout(raw: unknown): IntelLayout | null {
  const layout = (raw as { layout?: unknown } | null)?.layout;
  if (!layout || typeof layout !== "object") return null;
  const l = layout as Record<string, unknown>;
  return {
    intel: readTabLayout(l.intel),
    points: readTabLayout(l.points),
    updatedAt: typeof l.updatedAt === "string" ? l.updatedAt : undefined,
  };
}

/**
 * The units the screen draws, in the applied order: a key's unit is its
 * group (e.g. the Attached notes card) or the key alone. A group sits where
 * its first key sits. Units with no visible key are left out.
 */
export function visibleUnits(
  ordered: readonly string[],
  isVisible: (key: string) => boolean,
  groups: readonly (readonly string[])[] = [],
): string[][] {
  const groupOf = new Map<string, readonly string[]>();
  for (const g of groups) for (const k of g) groupOf.set(k, g);
  const units: string[][] = [];
  const seen = new Set<string>();
  for (const key of ordered) {
    if (seen.has(key)) continue;
    const group = groupOf.get(key);
    const unit = group ? ordered.filter((k) => group.includes(k)) : [key];
    unit.forEach((k) => seen.add(k));
    if (unit.some(isVisible)) units.push(unit);
  }
  return units;
}

/**
 * The new full order after the visible units are rearranged: the visible
 * units' keys fill the slots those keys held, in the new unit order; hidden
 * keys stay exactly where they were.
 */
function refill(ordered: readonly string[], before: string[][], after: string[][]): string[] {
  const moving = new Set(before.flat());
  const sequence = after.flat();
  let i = 0;
  return ordered.map((k) => (moving.has(k) ? sequence[i++] : k));
}

/** Where the unit holding `key` is, or -1. */
export function unitIndexOf(units: readonly string[][], key: string): number {
  return units.findIndex((u) => u.includes(key));
}

/** Can the unit holding `key` go up (or down)? */
export function canMove(units: readonly string[][], key: string, action: MoveAction): boolean {
  const i = unitIndexOf(units, key);
  if (i < 0) return false;
  return action === "down" ? i < units.length - 1 : i > 0;
}

/** Move up / Move down / Move to top for the unit holding `key`. Same array back when nothing moves. */
export function moveSection(
  ordered: readonly string[],
  units: string[][],
  key: string,
  action: MoveAction,
): string[] {
  const from = unitIndexOf(units, key);
  if (!canMove(units, key, action)) return [...ordered];
  const to = action === "top" ? 0 : action === "up" ? from - 1 : from + 1;
  return dropSection(ordered, units, key, units[to][0]);
}

/**
 * Drag and drop: the dragged unit takes the target unit's place, the units
 * in between shift by one (dragged down = it lands after the target, up =
 * before).
 */
export function dropSection(
  ordered: readonly string[],
  units: string[][],
  draggedKey: string,
  targetKey: string,
): string[] {
  const from = unitIndexOf(units, draggedKey);
  const to = unitIndexOf(units, targetKey);
  if (from < 0 || to < 0 || from === to) return [...ordered];
  const next = [...units];
  const [unit] = next.splice(from, 1);
  next.splice(to, 0, unit);
  return refill(ordered, units, next);
}

/**
 * The tab value one save sends: this meeting's keys first (full order,
 * collapsed in that order), then the previously saved keys this meeting
 * does not have, in their saved order (another meeting's topics, a section
 * this app version does not know). Over the cap, drop from the end (the
 * oldest other-meeting topics).
 */
export function buildTabSave(
  tab: IntelLayoutTab,
  present: readonly string[],
  ordered: readonly string[],
  collapsed: ReadonlySet<string>,
  saved: TabLayout | null,
): TabLayout {
  const cap = TAB_KEY_CAP[tab];
  const here = new Set(present);
  const uniq = (list: string[]) => [...new Set(list)];
  const order = uniq([...ordered.filter((k) => here.has(k)), ...(saved?.order ?? []).filter((k) => !here.has(k))]);
  const shut = uniq([
    ...ordered.filter((k) => here.has(k) && collapsed.has(k)),
    ...(saved?.collapsed ?? []).filter((k) => !here.has(k)),
  ]);
  return { order: order.slice(0, cap), collapsed: shut.slice(0, cap) };
}
